"use client";

import { useT, useGameLocale } from "@/lib/i18n";
import {
  useMemo,
  useState,
  useEffect,
  type MouseEvent as ReactMouseEvent,
  type CSSProperties,
} from "react";
import { useParams } from "next/navigation";
import { Link, useRouter } from "@/i18n/navigation";
import type { Relic } from "@/lib/api/types";
import RichDescription from "@/app/components/RichDescription";
import { cachedFetch } from "@/lib/fetch-cache";
import LocalizedNames from "@/app/components/LocalizedNames";
import EntityUpdateHistory from "@/app/components/EntityUpdateHistory";
import RelatedItems from "@/app/components/RelatedItems";
import EntityProse from "@/app/components/EntityProse";
import EntityPairings from "@/app/components/EntityPairings";
import EntityDraftRecs from "@/app/components/EntityDraftRecs";
import EntitySummary from "@/app/components/EntitySummary";
import type { EntitySummaryData } from "@/lib/entity-summary";
import type { DraftRecs, Pairings, RelatedGroup } from "@/lib/entity-links";
import EntityRunStats, {
  type EntityStats,
} from "@/app/components/EntityRunStats";
import EntityVersionSelect from "@/app/components/EntityVersionSelect";
import { imageUrl } from "@/lib/image-url";
import { useBetaPrefix } from "@/lib/api/prefix.client";
import BetaDiffNotice from "@/app/components/BetaDiffNotice";
import { keywordLinkWords, type EntityNameLink } from "@/lib/rich-links";
import type { KeywordEntry } from "@/lib/entity-catalogs";
import "@/app/card-revamp.css";
import "@/app/relic-potion-extra.css";
import { useBracketParam } from "@/lib/use-bracket-param";

const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";

// Headline figures for the infobox mini-stats block. Same endpoint
// EntityRunStats fetches, so cachedFetch dedupes it (no extra request).
interface MiniBracket {
  picks: number;
  win_rate: number;
  pick_rate: number;
  score: number | null;
  elo: number | null;
}
interface MiniStats extends MiniBracket {
  brackets?: Record<string, MiniBracket>;
}

export default function RelicDetail({
  initialRelic,
  initialStats,
  initialPairings,
  initialRecs,
  initialRelated,
  summary = null,
  initialEntityLinks,
  initialKeywords,
}: {
  initialRelic?: Relic | null;
  initialStats?: EntityStats | null;
  initialPairings?: Pairings | null;
  initialRecs?: DraftRecs | null;
  initialRelated?: RelatedGroup[] | null;
  summary?: EntitySummaryData | null;
  /** Server-fetched card name links for the description; absent on the beta page. */
  initialEntityLinks?: EntityNameLink[];
  /** Server-fetched keyword catalog for the description tooltip words. */
  initialKeywords?: KeywordEntry[];
} = {}) {
  const { id } = useParams<{ id: string }>();
  const router = useRouter();
  const lang = useGameLocale();
  const t = useT();
  const bp = useBetaPrefix();
  // Keyword tooltip words and card name links, built once per render from
  // the server-fetched catalogs (absent on the beta page: no links).
  const interactiveWords = useMemo(
    () =>
      initialKeywords && initialKeywords.length > 0
        ? keywordLinkWords(initialKeywords, bp)
        : undefined,
    [initialKeywords, bp],
  );
  const entityLinks = useMemo(
    () => (initialEntityLinks?.length ? initialEntityLinks : undefined),
    [initialEntityLinks],
  );
  const [relic, setRelic] = useState<Relic | null>(initialRelic ?? null);
  const [selectedVariant, setSelectedVariant] = useState<string | null>(null);
  const [selectedChar, setSelectedChar] = useState<string | null>(null);
  const [loading, setLoading] = useState(!initialRelic);
  const [notFound, setNotFound] = useState(false);
  // Scroll-spy: which section the ToC highlights.
  const [activeSection, setActiveSection] = useState<string>("performance");
  const [miniStats, setMiniStats] = useState<MiniStats | null>(null);
  // Bracket shared with EntityRunStats so the infobox mini-stats track the
  // pill the user picked in the Community section.
  const [statsBracket, setStatsBracket] = useBracketParam();

  useEffect(() => {
    if (!id) return;
    cachedFetch<Relic>(`${API}/api/relics/${id}?lang=${lang}`)
      .then((data) => {
        setRelic(data);
        if (data.image_variants) {
          const first = Object.entries(data.image_variants)[0];
          if (first) {
            setSelectedVariant(first[1]);
            setSelectedChar(first[0]);
          }
        }
      })
      .catch(() => {
        if (!initialRelic) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [id, lang]);

  // Headline community numbers for the infobox mini block. Hits the same URL
  // EntityRunStats fetches, so cachedFetch serves it from cache.
  useEffect(() => {
    if (!id) return;
    cachedFetch<MiniStats>(`${API}/api/runs/stats/relics/${id}`)
      .then(setMiniStats)
      .catch(() => {});
  }, [id]);

  // ToC scroll-spy: highlight the section currently in view.
  useEffect(() => {
    if (!relic) return;
    const secs = Array.from(
      document.querySelectorAll<HTMLElement>(".card-rvmp section[id]"),
    );
    if (secs.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveSection((e.target as HTMLElement).id);
        });
      },
      { rootMargin: "-130px 0px -70% 0px" },
    );
    secs.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, [relic]);

  const handleTocClick = (e: ReactMouseEvent, secId: string) => {
    e.preventDefault();
    const el = document.getElementById(secId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(secId);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center text-[var(--text-muted)]">
        {t("Loading...")}
      </div>
    );
  }

  if (notFound || !relic) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <p className="text-[var(--text-muted)] mb-4">{t("Relic not found.")}</p>
        <Link
          href={`${bp}/relics`}
          className="text-[var(--accent-gold)] hover:underline"
        >
          &larr; {t("Back to")} {t("Relics")}
        </Link>
      </div>
    );
  }

  const renderSrc = selectedVariant || relic.image_url;
  // Plain-text lede from the relic effect (rich tags + newlines stripped).
  const ledeText = relic.description
    ? relic.description
        .replace(/\[energy:(\d+|X)\]/g, (_, n) => t("{n} Energy", { n }))
        .replace(/\[star:(\d+|X)\]/g, (_, n) => t("{n} Star", { n }))
        .replace(/\[[^\]]*\]/g, "")
        .replace(/\s+/g, " ")
        .trim()
    : "";

  const hasImageVariants =
    !!relic.image_variants && Object.keys(relic.image_variants).length > 0;
  const hasNameVariants =
    !!relic.name_variants && Object.keys(relic.name_variants).length > 0;
  const hasNotes = !!relic.notes && relic.notes.length > 0;

  const tocItems: { id: string; label: string }[] = [
    { id: "performance", label: t("Community") },
    { id: "description", label: t("Description") },
    { id: "relations", label: t("Relations") },
    { id: "pairings", label: t("Synergy") },
    { id: "history", label: t("Version history") },
  ];

  return (
    <div
      className="card-rvmp"
      style={
        {
          "--spine": "var(--accent-gold)",
          ...(renderSrc
            ? { "--entity-bg": `url("${imageUrl(renderSrc)}?bg")` }
            : {}),
        } as CSSProperties
      }
    >
      <div className="cd-top">
        <button onClick={() => router.back()} className="cd-back">
          &larr; {t("Back to")} {t("Relics")}
        </button>
        <div style={{ marginTop: 12 }}>
          <BetaDiffNotice entityType="relics" entityId={relic.id} />
        </div>
      </div>

      <div className="wrap">
        {/* ===== MAIN column: unrolled sections ===== */}
        <main className="main">
          {/* Hero */}
          <div className="hero">
            <p className="eyebrow">
              <span className="dot">&#9670;</span>
              <span>{relic.rarity}</span>
              <span className="sep">&middot;</span>
              <span>{relic.pool}</span>
              <span className="sep">&middot;</span>
              <span>{t("Relic")}</span>
            </p>
            <h1>{relic.name}</h1>
            {ledeText && <p className="lede">{ledeText}</p>}
          </div>

          {/* Sticky ToC */}
          <nav className="toc" aria-label={t("On this page")}>
            <span className="toc-label">{t("On this page")}</span>
            {tocItems.map((it) => (
              <a
                key={it.id}
                href={`#${it.id}`}
                className={activeSection === it.id ? "on" : undefined}
                onClick={(e) => handleTocClick(e, it.id)}
              >
                {it.label}
              </a>
            ))}
          </nav>

          {/* Community performance (featured first) */}
          <section id="performance">
            <h2>{t("Community performance")}</h2>
            <p className="h-note">
              {t(
                "Live aggregate across community-submitted runs. Filter by bracket to see how it holds up at higher levels of play.",
              )}
            </p>
            <EntityRunStats
              entityType="relics"
              entityId={id}
              entityName={relic.name}
              variant="wiki"
              initialStats={initialStats}
              bracket={statsBracket}
              onBracketChange={setStatsBracket}
            />
          </section>

          {/* Description */}
          <section id="description">
            <h2>{t("Description")}</h2>
            <EntitySummary name={relic.name} data={summary} />
            <div className="desc-quote">
              <RichDescription
                text={relic.description}
                interactiveWords={interactiveWords}
                entityLinks={entityLinks}
              />
            </div>

            {relic.flavor && (
              <div className="desc-body rp-flavor">
                <RichDescription
                  text={relic.flavor}
                  interactiveWords={interactiveWords}
                  entityLinks={entityLinks}
                />
              </div>
            )}

            {/* Per-character display name overrides, Sea Glass renames itself
                ("Demon Glass" for Ironclad, "Venom Glass" for Silent, etc.). */}
            {hasNameVariants && (
              <>
                <h3 className="subh">{t("Known as")}</h3>
                <div className="chips">
                  {Object.entries(relic.name_variants!).map(
                    ([char, variantName]) => (
                      <span key={char} className="chip">
                        <span>{variantName}</span>
                        <span className="rp-alias">{char}</span>
                      </span>
                    ),
                  )}
                </div>
              </>
            )}

            {hasNotes && (
              <>
                <h3 className="subh">{t("Mechanics")}</h3>
                <ul className="rp-notes">
                  {relic.notes!.map((note, i) => (
                    <li key={i}>{note}</li>
                  ))}
                </ul>
              </>
            )}

            {/* Programmatic prose block, adds 60-100 words of factual
                contextual content per page from already-localized fields. */}
            <EntityProse kind="relic" relic={relic} />
          </section>

          {/* Relations, related relics from the same pool + rarity */}
          <section id="relations">
            <h2>{t("Relations")}</h2>
            <p className="h-note">
              {t("Other relics from the same pool and rarity.")}
            </p>
            <RelatedItems
              currentId={id}
              route="relics"
              heading="Related Relics"
              groups={[
                {
                  label: t("{pool} relics", { pool: relic.pool }),
                  path: `/api/relics?pool=${encodeURIComponent(relic.pool)}&lang=${lang}`,
                },
                {
                  label: relic.rarity.endsWith("Relic")
                    ? `${relic.rarity}s`
                    : t("{rarity} Relics", { rarity: relic.rarity }),
                  path: `/api/relics?rarity=${encodeURIComponent(relic.rarity)}&lang=${lang}`,
                },
              ]}
              initialGroups={initialRelated}
            />
          </section>

          <EntityPairings
            kind="relics"
            id={id}
            name={relic.name}
            lang={lang}
            bp={bp}
            initial={initialPairings}
          />

          <EntityDraftRecs
            kind="relics"
            id={id}
            name={relic.name}
            lang={lang}
            bp={bp}
            initial={initialRecs}
          />

          {/* Version history + localized names */}
          <LocalizedNames entityType="relics" entityId={id} />
          <EntityUpdateHistory entityType="relics" entityId={id} />
        </main>

        {/* ===== INFOBOX column (sticky) ===== */}
        <aside className="aside">
          <div className="box">
            {renderSrc && (
              <img
                className="cardimg render relimg"
                src={imageUrl(selectedVariant || relic.image_url || "")}
                alt={`${relic.name}${selectedChar ? ` (${selectedChar})` : ""} - Slay the Spire 2 Relic`}
                crossOrigin="anonymous"
              />
            )}

            {/* Per-character / per-save art switcher. Single layout for both
                variant types: buttons row, then a single italic hint below. */}
            {hasImageVariants &&
              (() => {
                const CHARACTER_KEYS = new Set([
                  "Ironclad",
                  "Silent",
                  "Defect",
                  "Necrobinder",
                  "Regent",
                ]);
                const variantKeys = Object.keys(relic.image_variants!);
                const isCharacterVariants = variantKeys.every((k) =>
                  CHARACTER_KEYS.has(k),
                );
                const hint = isCharacterVariants
                  ? t(
                      "This relic has different art for each character. Use buttons above.",
                    )
                  : t("Multiple in-game art variants, toggle above");
                return (
                  <div>
                    <div className="rp-variants">
                      {Object.entries(relic.image_variants!).map(
                        ([variantKey, url]) => (
                          <button
                            key={variantKey}
                            onClick={() => {
                              setSelectedVariant(url);
                              setSelectedChar(variantKey);
                            }}
                            title={`${t("Show")} ${variantKey} ${t("variant")}`}
                            className={`rp-vbtn${selectedVariant === url ? " on" : ""}`}
                          >
                            {variantKey}
                          </button>
                        ),
                      )}
                    </div>
                    <p className="rp-hint">{hint}</p>
                  </div>
                );
              })()}

            <div className="mb-2 text-center">
              <EntityVersionSelect
                entityType="relics"
                entityId={id}
                bracket={statsBracket}
                onBracketChange={setStatsBracket}
                onEntityData={(d) => {
                  if (d) setRelic(d as Relic);
                }}
              />
            </div>

            {/* Facts table */}
            <div className="facts">
              <div className="fh">{t("At a glance")}</div>
              <dl>
                <div className="frow">
                  <dt>{t("Rarity")}</dt>
                  <dd>{relic.rarity}</dd>
                </div>
                <div className="frow">
                  <dt>{t("Pool")}</dt>
                  <dd style={{ textTransform: "capitalize" }}>{relic.pool}</dd>
                </div>
                <div className="frow">
                  <dt>{t("Merchant Price")}</dt>
                  <dd>
                    {relic.merchant_price ? (
                      <>
                        <img
                          src={imageUrl(
                            "/static/images/ui/rewards/reward_icon_money.webp",
                          )}
                          alt={t("Gold")}
                          style={{ width: 15, height: 15 }}
                          crossOrigin="anonymous"
                        />
                        {relic.merchant_price.min}&ndash;
                        {relic.merchant_price.max}
                      </>
                    ) : (
                      t("Not sold")
                    )}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Community mini-stats — scoped to the bracket picked in the
                Community section (falls back to the all-runs figures). */}
            {(() => {
              const mini = miniStats?.brackets?.[statsBracket] ?? miniStats;
              if (!mini || mini.picks <= 0) return null;
              return (
                <div className="mini">
                  <div className="mh">{t("Community")}</div>
                  <div className="mg">
                    <div>
                      <div
                        className="mv"
                        style={{
                          color:
                            mini.win_rate >= 50 ? "var(--good)" : "var(--warn)",
                        }}
                      >
                        {mini.win_rate}%
                      </div>
                      <div className="ml">{t("Win rate")}</div>
                    </div>
                    <div>
                      <div className="mv">{mini.pick_rate}%</div>
                      <div className="ml">{t("Pick rate")}</div>
                    </div>
                    {mini.score != null && (
                      <div>
                        <div className="mv">{mini.score}</div>
                        <div className="ml">{t("Codex Score")}</div>
                      </div>
                    )}
                    {mini.elo != null && (
                      <div>
                        <div className="mv">{Math.round(mini.elo)}</div>
                        <div className="ml">{t("Elo")}</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </aside>
      </div>
    </div>
  );
}
